import { useRef } from "react";
import { Button, Stack } from "@mui/material";
import LogoRelatorio from "../../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../../Utils/useNomeArquivoPdf";

const ESTILOS_IMPRESSAO = `
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #172033; font-family: Arial, sans-serif; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .ocorrencias-relatorio-fonte { position: absolute; visibility: hidden; width: 186mm !important; max-width: none !important; padding: 0 !important; }
  .ocorrencias-relatorio-pagina { width: 210mm; height: 297mm; padding: 12mm; background: #fff; break-after: page; page-break-after: always; }
  .ocorrencias-relatorio-pagina:last-child { break-after: auto; page-break-after: auto; }
  .ocorrencias-relatorio-cabecalho { position: static; display: grid; grid-template-columns: 30mm 1fr 30mm; align-items: center; border: 1px solid #173b5f; margin: 0 0 12px; }
  .ocorrencias-relatorio-logo, .ocorrencias-relatorio-identificacao { padding: 8px; text-align: center; }
  .ocorrencias-relatorio-logo { align-self: stretch; display: flex; align-items: center; justify-content: center; border-right: 1px solid #173b5f; }
  .ocorrencias-relatorio-logo img { max-width: 26mm; max-height: 16mm; }
  .ocorrencias-relatorio-identificacao { align-self: stretch; display: flex; flex-direction: column; justify-content: center; padding: 0; border-left: 1px solid #173b5f; font-size: 9px; }
  .ocorrencias-relatorio-identificacao strong, .ocorrencias-relatorio-identificacao span { display: block; padding: 4px 2px; }
  .ocorrencias-relatorio-identificacao span { border-top: 1px solid #173b5f; }
  .ocorrencias-relatorio-titulo { padding: 8px; text-align: center; }
  .ocorrencias-relatorio-titulo strong { display: block; font-size: 15px; color: #0b4b78; }
  .ocorrencias-relatorio-titulo span { display: block; margin-top: 3px; font-size: 10px; }
  .ocorrencias-relatorio-resumo { display: flex; gap: 18px; margin: 0 0 12px; padding: 8px; background: #edf3f8; border: 1px solid #c4d3df; font-size: 10px; }
  .ocorrencias-relatorio-registro { break-inside: avoid; page-break-inside: avoid; margin-bottom: 10px; border: 1px solid #6f879a; }
  .ocorrencias-relatorio-registro-titulo { display: flex; justify-content: space-between; gap: 12px; padding: 7px 9px; background: #164d78; color: #fff; font-size: 11px; font-weight: bold; }
  .ocorrencias-relatorio-grade { display: grid; grid-template-columns: 1fr 1fr; }
  .ocorrencias-relatorio-campo { min-height: 38px; padding: 6px 8px; border-top: 1px solid #d4dde4; font-size: 10px; white-space: pre-wrap; overflow-wrap: anywhere; }
  .ocorrencias-relatorio-campo:nth-child(odd) { border-right: 1px solid #d4dde4; }
  .ocorrencias-relatorio-campo-largo { grid-column: 1 / -1; border-right: 0 !important; }
  .ocorrencias-relatorio-campo strong { display: block; margin-bottom: 3px; color: #24445f; font-size: 9px; text-transform: uppercase; }
  .ocorrencias-relatorio-campo a { color: #075a9c; }
`;

const estiloPagina = { maxWidth: 900, margin: "0 auto", padding: "20px", background: "#fff", color: "#172033", fontFamily: "Arial, sans-serif" };
const estiloCabecalho = { display: "grid", gridTemplateColumns: "110px 1fr 110px", alignItems: "center", border: "1px solid #173b5f", marginBottom: 12 };
const estiloCelulaCabecalho = { padding: 8, textAlign: "center" };
const estiloResumo = { display: "flex", gap: 18, marginBottom: 12, padding: 8, background: "#edf3f8", border: "1px solid #c4d3df", fontSize: 12 };
const estiloRegistro = { marginBottom: 10, border: "1px solid #6f879a", breakInside: "avoid" };
const estiloTituloRegistro = { display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 9px", background: "#164d78", color: "#fff", fontSize: 12, fontWeight: "bold" };
const estiloGrade = { display: "grid", gridTemplateColumns: "1fr 1fr" };
const estiloCampo = { minHeight: 42, padding: "6px 8px", borderTop: "1px solid #d4dde4", fontSize: 12, whiteSpace: "pre-wrap", overflowWrap: "anywhere" };
const estiloRotulo = { display: "block", marginBottom: 3, color: "#24445f", fontSize: 10, textTransform: "uppercase" };

function valor(conteudo) {
  return String(conteudo ?? "").trim() || "-";
}

function dataFormatada(data) {
  const dataConvertida = data?.toDate?.() || (data instanceof Date ? data : null);
  return dataConvertida && !Number.isNaN(dataConvertida.getTime())
    ? dataConvertida.toLocaleDateString("pt-BR")
    : "-";
}

function prazoFormatado(prazo) {
  const partes = String(prazo || "").split("-");
  return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : valor(prazo);
}

function tipoFormatado(registro) {
  if (registro.tipoRegistro === "MELHORIA") return "Melhoria";
  if (registro.tipoRegistro === "OCORRENCIA") return "Ocorrência";
  if (registro.tipoRegistro === "INSPECAO_AMBIENTES") return "Inspeção de Ambientes";
  if (registro.tipoRegistro !== "NC") return valor(registro.tipoRegistro);
  const classificacao = valor(registro.classificacaoNC).replaceAll("_", " ");
  return `Não Conformidade • ${classificacao === "-" ? "Dia a dia" : classificacao}`;
}

function Campo({ rotulo, children, largo = false }) {
  return (
    <div
      className={`ocorrencias-relatorio-campo${largo ? " ocorrencias-relatorio-campo-largo" : ""}`}
      style={{ ...estiloCampo, ...(largo ? { gridColumn: "1 / -1" } : {}) }}
    >
      <strong style={estiloRotulo}>{rotulo}</strong>
      {children}
    </div>
  );
}

function salvarComoPdf(elemento, nomeArquivo) {
  if (!elemento) return;
  document.getElementById("ocorrencias-relatorio-iframe")?.remove();

  const iframe = document.createElement("iframe");
  iframe.id = "ocorrencias-relatorio-iframe";
  iframe.title = "Relatório de não conformidades e melhorias";
  Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "1px", height: "1px", border: "0", opacity: "0", pointerEvents: "none" });
  document.body.appendChild(iframe);

  const documento = iframe.contentDocument;
  const janela = iframe.contentWindow;
  documento.open();
  documento.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${nomeArquivo}</title><style>${ESTILOS_IMPRESSAO}</style></head><body></body></html>`);
  documento.close();

  const fonte = elemento.cloneNode(true);
  fonte.classList.add("ocorrencias-relatorio-fonte");
  documento.body.appendChild(fonte);

  const imagens = Array.from(documento.images);
  Promise.all(imagens.map((imagem) => imagem.complete ? Promise.resolve() : new Promise((resolve) => {
    imagem.addEventListener("load", resolve, { once: true });
    imagem.addEventListener("error", resolve, { once: true });
  }))).then(() => {
    const cabecalho = fonte.querySelector(".ocorrencias-relatorio-cabecalho");
    const resumo = fonte.querySelector(".ocorrencias-relatorio-resumo");
    const registros = Array.from(fonte.querySelectorAll(".ocorrencias-relatorio-registro"));
    documento.body.innerHTML = "";

    function criarPagina() {
      const pagina = documento.createElement("section");
      pagina.className = "ocorrencias-relatorio-pagina";
      pagina.appendChild(cabecalho.cloneNode(true));
      documento.body.appendChild(pagina);
      return pagina;
    }

    function ultrapassouPagina(pagina, registro) {
      const estilosPagina = janela.getComputedStyle(pagina);
      const limiteInferior = pagina.getBoundingClientRect().bottom - parseFloat(estilosPagina.paddingBottom);
      return registro.getBoundingClientRect().bottom > limiteInferior + 0.5;
    }

    let paginaAtual = criarPagina();
    if (resumo) paginaAtual.appendChild(resumo.cloneNode(true));

    registros.forEach((registroOriginal) => {
      let registro = registroOriginal.cloneNode(true);
      const paginaJaTemConteudo = Boolean(
        paginaAtual.querySelector(".ocorrencias-relatorio-resumo, .ocorrencias-relatorio-registro")
      );
      paginaAtual.appendChild(registro);

      if (ultrapassouPagina(paginaAtual, registro) && paginaJaTemConteudo) {
        registro.remove();
        paginaAtual = criarPagina();
        registro = registroOriginal.cloneNode(true);
        paginaAtual.appendChild(registro);
      }
    });

    janela.requestAnimationFrame(() => {
      janela.onafterprint = () => iframe.remove();
      janela.focus();
      janela.print();
    });
  });
}

export default function RelatorioOcorrenciasPrint({ registros, filtros, onVoltar, individual = false }) {
  const documentoRef = useRef(null);
  const nomeArquivo = individual
    ? `RQ-1.3-OCORRENCIA-${registros[0]?.id || "REGISTRO"}`
    : "RQ-1.3-NAO-CONFORMIDADES-E-MELHORIAS";
  useNomeArquivoPdf(nomeArquivo);

  return (
    <div style={{ padding: "20px 12px", background: "#eef2f5", minHeight: "100vh" }}>
      <Stack direction="row" spacing={1} justifyContent="center" mb={2}>
        <Button variant="outlined" onClick={onVoltar}>Voltar</Button>
        <Button variant="contained" onClick={() => salvarComoPdf(documentoRef.current, nomeArquivo)}>Salvar como PDF</Button>
      </Stack>

      <main ref={documentoRef} className="ocorrencias-relatorio-documento" style={estiloPagina}>
        <header className="ocorrencias-relatorio-cabecalho" style={estiloCabecalho}>
          <div className="ocorrencias-relatorio-logo" style={{ ...estiloCelulaCabecalho, alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center", borderRight: "1px solid #173b5f" }}><LogoRelatorio alt="Logo da empresa" /></div>
          <div className="ocorrencias-relatorio-titulo" style={estiloCelulaCabecalho}>
            <strong style={{ display: "block", fontSize: 17, color: "#0b4b78" }}>NÃO CONFORMIDADES E MELHORIAS</strong>
            <span style={{ display: "block", marginTop: 3, fontSize: 11 }}>Sistema de Gestão da Qualidade</span>
          </div>
          <div className="ocorrencias-relatorio-identificacao" style={{ alignSelf: "stretch", display: "flex", flexDirection: "column", justifyContent: "center", padding: 0, textAlign: "center", borderLeft: "1px solid #173b5f", fontSize: 10 }}>
            <strong style={{ display: "block", padding: "4px 2px" }}>RQ 1.3</strong>
            <span style={{ display: "block", padding: "4px 2px", borderTop: "1px solid #173b5f" }}>REVISÃO 01</span>
            <span style={{ display: "block", padding: "4px 2px", borderTop: "1px solid #173b5f" }}>20/08/2025</span>
          </div>
        </header>

        {!individual && (
          <section className="ocorrencias-relatorio-resumo" style={estiloResumo}>
            <span><strong>Tipo:</strong> {valor(filtros?.tipo)}</span>
            <span><strong>Status:</strong> {valor(filtros?.status)}</span>
            <span><strong>Registros:</strong> {registros.length}</span>
          </section>
        )}

        {registros.map((registro, indice) => (
          <section key={registro.id || indice} className="ocorrencias-relatorio-registro" style={estiloRegistro}>
            <div className="ocorrencias-relatorio-registro-titulo" style={estiloTituloRegistro}>
              <span>{individual ? "Detalhes do registro" : `Registro ${indice + 1}`}</span>
              <span>{tipoFormatado(registro)} — {valor(registro.status)}</span>
            </div>
            <div className="ocorrencias-relatorio-grade" style={estiloGrade}>
              <Campo rotulo="Relator">{valor(registro.userNome)}</Campo>
              <Campo rotulo="Data do registro">{dataFormatada(registro.criadoEm)}</Campo>
              <Campo rotulo="Relato" largo>{valor(registro.descricao)}</Campo>
              <Campo rotulo="Referência">{valor(registro.referencia)}</Campo>
              <Campo rotulo="Classificação">{valor(registro.classificacao)}</Campo>
              {registro.tipoRegistro === "NC" && <Campo rotulo="Tipo da não conformidade">{valor(registro.tipoNC)}</Campo>}
              <Campo rotulo="Prazo">{prazoFormatado(registro.prazo)}</Campo>
              <Campo rotulo="Causa" largo>{valor(registro.causa)}</Campo>
              <Campo rotulo="Ação corretiva" largo>{valor(registro.acaoCorretiva)}</Campo>
              <Campo rotulo="Ação preventiva" largo>{valor(registro.acaoPreventiva)}</Campo>
              <Campo rotulo="Evidência" largo>
                {registro.imagemUrl ? <a href={registro.imagemUrl} target="_blank" rel="noopener noreferrer">Abrir anexo do registro</a> : "-"}
              </Campo>
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
